/**
 * AttributionContext — Slice 1 AT-01c.
 *
 * Thin composition layer over `useStore()` + the extension attribution
 * bridge. Exposes UI-facing attribution APIs — NOT raw namespace plumbing.
 *
 * Per D23.9: the PWA reads attribution state via the extension
 * (authoritative Slice 1 runtime). `StoreContext` is NOT made
 * protocol-aware; `DataSourceContext` is NOT extended.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useStore } from './StoreContext';
import {
  AttributionBridge,
  type AttributionBridgeOptions,
  type BroadcastPayload,
  type DeliveryLogEntry,
  type DisclosureSnapshot,
  type PaymentLogEntry,
  type RecordInstallConsentInput,
  type RecordInstallConsentResult,
} from '../services/attribution-bridge';

const HITL_QA_TOOLS_ENABLED = import.meta.env.DEV || import.meta.env.VITE_ENABLE_HITL_QA_TOOLS === 'true';

export type InstallConsentStatus = 'loading' | 'recorded' | 'absent' | 'error';

export interface AttributionContextValue {
  /** Storage substrate readiness — surfaced from StoreContext (composition, not bypass). */
  storeReady: boolean;
  /** Install-consent gate status. */
  installConsentStatus: InstallConsentStatus;
  /** Recorded-at timestamp when status === 'recorded'. */
  installConsentRecordedAt: number | null;
  /** Wallet's current disclosure snapshot (read-only in Slice 1). */
  disclosureSnapshot: DisclosureSnapshot | null;
  /** Payment-log summary (rendered by Wallet Earnings panel in AT-06). */
  paymentLog: PaymentLogEntry[];
  /** Delivery-log summary (rendered by RawData diagnostics in AT-04). */
  deliveryLog: DeliveryLogEntry[];
  /** Last error surfaced by the bridge (network timeout / extension absent). */
  bridgeError: string | null;
  /** Record install consent via the bridge; returns the bridge's response. */
  recordInstallConsent: (input: RecordInstallConsentInput) => Promise<RecordInstallConsentResult>;
  /** Force a re-pull of install-consent + disclosure + logs from the extension. */
  refresh: () => Promise<void>;
}

const AttributionContext = createContext<AttributionContextValue | null>(null);

export interface AttributionProviderProps {
  children: ReactNode;
  /** Injected bridge for tests; defaults to a fresh AttributionBridge. */
  bridge?: AttributionBridge;
  bridgeOptions?: AttributionBridgeOptions;
  /** Skip initial pull (tests). */
  skipInitialFetch?: boolean;
}

export function AttributionProvider({
  children,
  bridge: injectedBridge,
  bridgeOptions,
  skipInitialFetch = false,
}: AttributionProviderProps) {
  const { isReady: storeReady } = useStore();
  const bridgeRef = useRef<AttributionBridge | null>(null);

  // Initialise bridge once per provider mount.
  if (bridgeRef.current === null) {
    bridgeRef.current = injectedBridge ?? new AttributionBridge(bridgeOptions);
  }

  const [installConsentStatus, setInstallConsentStatus] =
    useState<InstallConsentStatus>('loading');
  const [installConsentRecordedAt, setInstallConsentRecordedAt] = useState<number | null>(
    null,
  );
  const [disclosureSnapshot, setDisclosureSnapshot] = useState<DisclosureSnapshot | null>(
    null,
  );
  const [paymentLog, setPaymentLog] = useState<PaymentLogEntry[]>([]);
  const [deliveryLog, setDeliveryLog] = useState<DeliveryLogEntry[]>([]);
  const [bridgeError, setBridgeError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    const bridge = bridgeRef.current;
    if (!bridge) return;
    try {
      const consent = await bridge.getInstallConsentState();
      if (consent.recorded && consent.recorded_at !== undefined) {
        setInstallConsentStatus('recorded');
        setInstallConsentRecordedAt(consent.recorded_at);
      } else {
        setInstallConsentStatus('absent');
        setInstallConsentRecordedAt(null);
      }

      const [snapshot, earnings, deliveries] = await Promise.all([
        bridge.getDisclosureSnapshot().catch(() => ({ disclosure: null as DisclosureSnapshot | null })),
        bridge.getEarnings().catch(() => ({ paymentLog: [] as PaymentLogEntry[] })),
        bridge.getDeliveryLog().catch(() => ({ deliveryLog: [] as DeliveryLogEntry[] })),
      ]);
      const qaPaymentLog = await readQaPaymentLogFixtures();
      setDisclosureSnapshot(snapshot.disclosure);
      setPaymentLog([...earnings.paymentLog, ...qaPaymentLog]);
      setDeliveryLog(deliveries.deliveryLog);
      setBridgeError(null);
    } catch (err) {
      setInstallConsentStatus('error');
      setBridgeError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  // Initial pull (skippable for tests).
  useEffect(() => {
    if (skipInitialFetch) {
      setInstallConsentStatus('absent');
      return;
    }
    void refresh();
  }, [refresh, skipInitialFetch]);

  // Broadcast subscriptions.
  useEffect(() => {
    const bridge = bridgeRef.current;
    if (!bridge) return;
    const unsubDelivery = bridge.subscribe(
      'OWNYOU_DELIVERY_COMPLETED',
      (payload: BroadcastPayload) => {
        const entry = payload as unknown as DeliveryLogEntry;
        setDeliveryLog((prev) => [entry, ...prev].slice(0, 200));
      },
    );
    const unsubFailed = bridge.subscribe(
      'OWNYOU_DELIVERY_FAILED',
      (payload: BroadcastPayload) => {
        const entry = payload as unknown as DeliveryLogEntry;
        setDeliveryLog((prev) => [entry, ...prev].slice(0, 200));
      },
    );
    const unsubPayment = bridge.subscribe(
      'OWNYOU_PAYMENT_RECEIVED',
      (payload: BroadcastPayload) => {
        const entry = payload as unknown as PaymentLogEntry;
        setPaymentLog((prev) => [entry, ...prev].slice(0, 200));
      },
    );
    return () => {
      unsubDelivery();
      unsubFailed();
      unsubPayment();
    };
  }, []);

  useEffect(() => {
    return () => {
      bridgeRef.current?.destroy();
      bridgeRef.current = null;
    };
  }, []);

  const recordInstallConsent = useCallback(
    async (input: RecordInstallConsentInput): Promise<RecordInstallConsentResult> => {
      const bridge = bridgeRef.current;
      if (!bridge) {
        return { ok: false, error: 'bridge_unavailable' };
      }
      const result = await bridge.recordInstallConsent(input);
      if (result.ok) {
        setInstallConsentStatus('recorded');
        setInstallConsentRecordedAt(result.recorded_at);
      }
      return result;
    },
    [],
  );

  const value = useMemo<AttributionContextValue>(
    () => ({
      storeReady,
      installConsentStatus,
      installConsentRecordedAt,
      disclosureSnapshot,
      paymentLog,
      deliveryLog,
      bridgeError,
      recordInstallConsent,
      refresh,
    }),
    [
      storeReady,
      installConsentStatus,
      installConsentRecordedAt,
      disclosureSnapshot,
      paymentLog,
      deliveryLog,
      bridgeError,
      recordInstallConsent,
      refresh,
    ],
  );

  return (
    <AttributionContext.Provider value={value}>{children}</AttributionContext.Provider>
  );
}

export function useAttribution(): AttributionContextValue {
  const ctx = useContext(AttributionContext);
  if (!ctx) {
    throw new Error('useAttribution must be used inside an <AttributionProvider>');
  }
  return ctx;
}

/**
 * Same as `useAttribution()` but returns `null` instead of throwing when no
 * provider is mounted. Used by surfaces (e.g. RawData diagnostics) that want
 * to render gracefully when the AttributionProvider hasn't been wired into
 * the app tree yet (AT-13 wires it in).
 */
export function useAttributionOptional(): AttributionContextValue | null {
  return useContext(AttributionContext);
}

async function readQaPaymentLogFixtures(): Promise<PaymentLogEntry[]> {
  if (!HITL_QA_TOOLS_ENABLED) return [];
  const { readSlice06QaSettlementFixtures } = await import('../lib/wallet/qa-fixtures');
  return readSlice06QaSettlementFixtures();
}
